// shared/components/stripe-payment-form/stripe-payment-form.component.ts
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  OnInit,
  TemplateRef,
  SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators, AbstractControl } from "@angular/forms";
import { Subject } from "rxjs";

declare var Stripe: any;

// Interfaces
export interface StripePaymentFormConfig {
  showName?: boolean;
  showEmail?: boolean;
  showPhone?: boolean;
  showAddress?: boolean;
  showZipCode?: boolean;
  showSaveCard?: boolean;
  showApplePay?: boolean;
  showGooglePay?: boolean;
  submitButtonText?: string;
  cancelButtonText?: string;
  formTitle?: string;
  formSubtitle?: string;
  stripePublicKey: string;
  locale?: string;
  applePayOptions?: {
    buttonType?: "plain" | "buy" | "set-up" | "donate" | "check-out" | "book" | "subscribe";
    buttonTheme?: "dark" | "light" | "light-outline";
    buttonHeight?: string;
  };
  save_button?: string;
}

export interface StripePaymentFormData {
  paymentMethodId?: string;
  billingDetails?: {
    name?: string;
    email?: string;
    phone?: string;
    address?: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
    };
  };
  saveCard?: boolean;
}

export interface StripeElementStyle {
  base: {
    color: string;
    fontSize: string;
    fontFamily: string;
    "::placeholder": {
      color: string;
    };
  };
  invalid: {
    color: string;
  };
}

@Component({
  selector: "app-stripe-payment-form",
  templateUrl: "./stripe-payment-form.component.html",
  styleUrls: ["./stripe-payment-form.component.scss"],
})
export class StripePaymentFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("cardNumber") cardNumberRef: ElementRef;
  @ViewChild("cardExpiry") cardExpiryRef: ElementRef;
  @ViewChild("cardCvc") cardCvcRef: ElementRef;

  @Input() config: StripePaymentFormConfig = {
    showName: true,
    showZipCode: true,
    submitButtonText: "Submit Payment",
    cancelButtonText: "Cancel",
    stripePublicKey: "",
    applePayOptions: {
      buttonType: "plain",
      buttonTheme: "dark",
      buttonHeight: "48px",
    },
  };
  @Input() stripeFormType: string = "";
  @Input() customContent: TemplateRef<any>;
  @Input() termsAccepted = true;
  @Input() totalPayable: number = 0;
  @Output() formSubmit = new EventEmitter<StripePaymentFormData>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() formError = new EventEmitter<string>();
  @Output() applePayComplete = new EventEmitter<StripePaymentFormData>();

  paymentForm: FormGroup;
  loading = false;
  showApplePayButton = false;
  showGooglePayButton = false;
  _termsAccepted: boolean = false;

  // Stripe elements
  private stripe: any;
  private elements: any;
  private cardNumber: any;
  private cardExpiry: any;
  private cardCvc: any;
  private paymentRequest: any;
  private paymentRequestButton: any;

  errors = {
    cardNumber: "",
    cardExpiry: "",
    cardCvc: "",
  };

  cardBrand: string = "";

  stripeElementsValid = {
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  };

  private destroy$ = new Subject<void>();

  // Default Stripe element styles
  private readonly STRIPE_ELEMENT_STYLE: StripeElementStyle = {
    base: {
      color: "#2f3542",
      fontSize: "16px",
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      "::placeholder": {
        color: "#999",
      },
    },
    invalid: {
      color: "#e74c3c",
    },
  };

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initializeForm();
    this._termsAccepted = this.termsAccepted;
  }

  ngAfterViewInit(): void {
    if (this.config.stripePublicKey) {
      this.initializeStripe();
    } else {
      this.formError.emit("Payment system configuration error");
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["termsAccepted"]) {
      this._termsAccepted = changes["termsAccepted"].currentValue;
    }

    if (changes["totalPayable"] && !changes["totalPayable"].firstChange) {
      this.updatePaymentRequest();
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unmountStripeElements();
  }

  private initializeForm(): void {
    const formControls: any = {};

    // Dynamically add form controls based on config
    if (this.config.showName) {
      formControls.name = ["", [Validators.required]];
    }

    if (this.config.showEmail) {
      formControls.email = ["", [Validators.required, Validators.email]];
    }

    if (this.config.showPhone) {
      formControls.phone = ["", [Validators.required]];
    }

    if (this.config.showAddress) {
      formControls.addressLine1 = ["", [Validators.required]];
      formControls.addressLine2 = [""];
      formControls.city = ["", [Validators.required]];
      formControls.state = ["", [Validators.required]];
      formControls.country = ["", [Validators.required]];
    }

    if (this.config.showZipCode) {
      formControls.zipCode = ["", [Validators.required, Validators.pattern(/^\d{5}$/)]];
    }

    if (this.config.showSaveCard) {
      formControls.saveCard = [false];
    }

    this.paymentForm = this.fb.group(formControls);
  }

  private initializeStripe(): void {
    this.stripe = Stripe(this.config.stripePublicKey, {
      locale: this.config.locale || "en",
    });

    this.elements = this.stripe.elements();
    this.createStripeElements();
    this.mountStripeElements();

    if (this.config.showApplePay || this.config.showGooglePay) {
      this.setupPaymentRequest();
    }
  }

  private createStripeElements(): void {
    this.cardNumber = this.elements.create("cardNumber", {
      style: this.STRIPE_ELEMENT_STYLE,
      showIcon: true,
      iconStyle: "solid",
    });

    this.cardExpiry = this.elements.create("cardExpiry", {
      style: this.STRIPE_ELEMENT_STYLE,
    });

    this.cardCvc = this.elements.create("cardCvc", {
      style: this.STRIPE_ELEMENT_STYLE,
    });
  }

  private mountStripeElements(): void {
    setTimeout(() => {
      if (this.cardNumberRef?.nativeElement) {
        this.cardNumber.mount(this.cardNumberRef.nativeElement);
        this.cardNumber.on("change", (event) => this.handleElementChange("cardNumber", event));
      }

      if (this.cardExpiryRef?.nativeElement) {
        this.cardExpiry.mount(this.cardExpiryRef.nativeElement);
        this.cardExpiry.on("change", (event) => this.handleElementChange("cardExpiry", event));
      }

      if (this.cardCvcRef?.nativeElement) {
        this.cardCvc.mount(this.cardCvcRef.nativeElement);
        this.cardCvc.on("change", (event) => this.handleElementChange("cardCvc", event));
      }
    }, 100);
  }

  private unmountStripeElements(): void {
    if (this.cardNumber) this.cardNumber.unmount();
    if (this.cardExpiry) this.cardExpiry.unmount();
    if (this.cardCvc) this.cardCvc.unmount();
  }

  private handleElementChange(element: string, event: any): void {
    this.errors[element] = event.error?.message || "";
    this.stripeElementsValid[element] = event.complete && !event.error;

    if (element === "cardNumber" && event.brand) {
      this.cardBrand = event.brand;
    }
  }

  private async setupPaymentRequest(): Promise<void> {
    this.paymentRequest = this.stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: "Payment",
        amount: this.totalPayable,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    const result = await this.paymentRequest.canMakePayment();
    if (result) {
      if (result.applePay && this.config.showApplePay) {
        this.showApplePayButton = true;
      }
      if (result.googlePay && this.config.showGooglePay) {
        this.showGooglePayButton = true;
      }

      if (this.showApplePayButton || this.showGooglePayButton) {
        this.createPaymentRequestButton();
      }
    }

    this.paymentRequest.on("paymentmethod", this.handlePaymentRequestPayment.bind(this));
  }

  private createPaymentRequestButton(): void {
    this.paymentRequestButton = this.elements.create("paymentRequestButton", {
      paymentRequest: this.paymentRequest,
      style: {
        paymentRequestButton: {
          theme: "dark",
          height: "48px",
          type: "default",
        },
      },
    });

    setTimeout(() => {
      const element = document.getElementById("apple-pay-button");
      if (element) {
        this.paymentRequestButton.mount("#apple-pay-button");
      }
    }, 100);
  }

  async handleSubmit(): Promise<void> {
    if (!this.isFormValid) {
      this.markFormGroupTouched(this.paymentForm);
      return;
    }

    this.loading = true;
    const formValues = this.paymentForm.value;

    try {
      const billingDetails: any = {};

      if (this.config.showName && formValues.name) {
        billingDetails.name = formValues.name;
      }

      if (this.config.showEmail && formValues.email) {
        billingDetails.email = formValues.email;
      }

      if (this.config.showPhone && formValues.phone) {
        billingDetails.phone = formValues.phone;
      }

      if (this.config.showAddress || this.config.showZipCode) {
        billingDetails.address = {};

        if (this.config.showAddress) {
          billingDetails.address.line1 = formValues.addressLine1;
          billingDetails.address.line2 = formValues.addressLine2;
          billingDetails.address.city = formValues.city;
          billingDetails.address.state = formValues.state;
          billingDetails.address.country = formValues.country;
        }

        if (this.config.showZipCode && formValues.zipCode) {
          billingDetails.address.postal_code = formValues.zipCode;
        }
      }

      const { paymentMethod, error } = await this.stripe.createPaymentMethod({
        type: "card",
        card: this.cardNumber,
        billing_details: billingDetails,
      });

      if (error) {
        this.formError.emit(error.message);
      } else {
        const paymentData: StripePaymentFormData = {
          paymentMethodId: paymentMethod.id,
          billingDetails: billingDetails,
        };

        if (this.config.showSaveCard) {
          paymentData.saveCard = formValues.saveCard;
        }

        if (this.stripeFormType == "resident-payment") this.formSubmit.emit(paymentMethod);
        else this.formSubmit.emit(paymentData);
      }
    } catch (error) {
      this.formError.emit("An unexpected error occurred");
    } finally {
      this.loading = false;
    }
  }

  onApplePayButtonClick(): void {
    if (!this.termsAccepted) {
      this.formError.emit("An unexpected error occurred");
      return;
    }
  }

  private async handlePaymentRequestPayment(event: any): Promise<void> {
    try {
      const paymentData: StripePaymentFormData = {
        paymentMethodId: event.paymentMethod.id,
        billingDetails: {
          name: event.payerName,
          email: event.payerEmail,
        },
      };

      this.applePayComplete.emit(paymentData);
      event.complete("success");
    } catch (error) {
      event.complete("fail");
      this.formError.emit("Payment failed");
    }
  }

  handleCancel(): void {
    this.formCancel.emit();
  }

  // Reset validation state
  resetValidation(): void {
    this.stripeElementsValid = {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    };
    this.errors = {
      cardNumber: "",
      cardExpiry: "",
      cardCvc: "",
    };
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  // Template helper methods
  hasError(field: string): boolean {
    const control = this.paymentForm.get(field);
    return !!(control?.invalid && control?.touched);
  }

  getError(field: string, error: string): boolean {
    const control = this.paymentForm.get(field);
    return !!(control?.hasError(error) && control?.touched);
  }

  // Check if all required fields are valid
  get isFormValid(): boolean {
    // Check if Angular form is valid
    const formValid = this.paymentForm.valid;

    // Check if all Stripe elements are valid
    const stripeElementsValid =
      this.stripeElementsValid.cardNumber && this.stripeElementsValid.cardExpiry && this.stripeElementsValid.cardCvc;
    const termsValid = this._termsAccepted;
    return formValid && stripeElementsValid && termsValid;
  }

  getValidationDetails(): any {
    return {
      formValid: this.paymentForm.valid,
      stripeElements: this.stripeElementsValid,
      termsAccepted: this._termsAccepted,
      isFormValid: this.isFormValid,
      formErrors: this.paymentForm.errors,
      stripeErrors: this.errors,
    };
  }

  isCardNumberValid(): boolean {
    return this.stripeElementsValid.cardNumber;
  }

  isCardExpiryValid(): boolean {
    return this.stripeElementsValid.cardExpiry;
  }

  isCardCvcValid(): boolean {
    return this.stripeElementsValid.cardCvc;
  }

  isZipCodeValid(): boolean {
    const zipCodeControl = this.paymentForm.get("zipCode");
    return zipCodeControl ? zipCodeControl.valid && zipCodeControl.touched : true;
  }

  private updatePaymentRequest(): void {
    if (this.paymentRequest && this.stripe) {
      this.paymentRequest.update({
        total: {
          label: "Payment",
          amount: this.totalPayable,
        },
      });
    }
  }
}
